import React, { useState, useRef } from 'react';
import styled from 'styled-components/macro';
import useOnClickOutside from 'use-onclickoutside';
import { getCurrentLanguage } from '../../i18n';
import { useTranslation } from 'react-i18next';

interface OpenProps {
    open: boolean;
}

const width = 27;
const height = 20;

const Wrapper = styled.div<OpenProps>`
    position: relative;
    top: 0px;
    right: 0px;
    z-index: ${(props) => (props.open ? '100' : '1')};
`;

const Selected = styled.button<OpenProps>`
    border: 1px solid transparent;
    margin: 0;
    padding: 0;
    background: transparent;

    width: ${width + 2}px;
    height: ${height}px;

    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    ${(props) =>
        props.open
            ? `
        z-index: 10;
        border: 1px solid #eee;
        background: #fff;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-top-left-radius: 5px;
        border-top-right-radius: 5px;
        border-bottom: none;
        `
            : ''}
    &:focus {
        outline: none;
        background: rgba(0, 0, 255, 0.05);
        border: 1px solid #eee;
        border-radius: 5px;

        ${(props) =>
        props.open
            ? `
                z-index: 10;
                background: rgba(0, 0, 255, 0.05);
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
                border-top-left-radius: 5px;
                border-top-right-radius: 5px;
                border-bottom: none;
            `
            : ''}
    }
`;

const MenuItem = styled.button`
    background: transparent;
    padding: 0;
    margin: 0;
    border: none;
    display: flex;

    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:focus {
        outline: none;
        background: #d0ffe0;
    }
`;

const Flag = styled.img`
    width: ${width}px;
    height: ${height}px;
    margin: 0;
    padding: 3px;
    cursor: pointer;
`;

const Menu = styled.div<OpenProps>`
    position: absolute;
    top: 16px;
    /* bottom: ${height}px; */

    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column-reverse;

    transform-origin: top center;
    transform: scaleY(0);
    transition: transform 0.1s;
    ${(props) => (props.open ? 'transform: scaleY(1);' : '')}

    background: #fff;
    border: 1px solid #eee;
    border-top: none;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
`;

const flags = [
    { code: 'no', Flag: <Flag src={`${process.env.PUBLIC_URL}/assets/flags/norway-colored-48-cropped.svg`} alt="Norsk" title="Norsk" /> },
    { code: 'en', Flag: <Flag src={`${process.env.PUBLIC_URL}/assets/flags/uk-colored-48-cropped.svg`} alt="English" title="English" /> },
    { code: 'sv', Flag: <Flag src={`${process.env.PUBLIC_URL}/assets/flags/se-colored-48-cropped.svg`} alt="Svenska" title="Svenska" /> },
];

interface LanguageProps {
    initial?: boolean;
}

const Language = (
    {
        initial = false,
    }: LanguageProps
) => {
    const [open, setOpen] = useState(initial);
    const ref = useRef(null);
    function closeMenu() {
        setOpen(false);
    }
    function openMenu() {
        setOpen(true);
    }
    useOnClickOutside(ref, closeMenu);
    const { t, i18n } = useTranslation();
    const language = getCurrentLanguage(i18n);
    const currentLanguage = flags.find(({ code }) => code === language);
    const otherLanguages = flags.filter(({ code }) => code !== language);

    return (
        <Wrapper open={open} ref={ref}>
            <Selected
                onClick={openMenu}
                open={open}
                aria-haspopup={true}
                aria-expanded={open}
                role="button"
                aria-label={t('change_language.title')}
            >
                {currentLanguage && currentLanguage.Flag}
            </Selected>
            <Menu open={open} role="menu">
                {otherLanguages.map(({ code, Flag }) => (
                    <MenuItem
                        key={code}
                        onClick={() => {
                            i18n.changeLanguage(code);
                            closeMenu();
                        }}
                        disabled={!open}
                        role="menuitem"
                        lang={code}
                        aria-label={code}
                    >
                        {Flag}
                    </MenuItem>
                ))}
            </Menu>
        </Wrapper>
    );
};

export default Language;
